import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Authors from 'components/misc/Authors'
import Reference from './Reference'
import { highlightText } from 'lib/highlight-text'
import HorizontalTally from '../misc/HorizontalTally'
import Link from 'components/misc/Link'
import { toggleModal, MODAL_FROM_CONTEXTS, setCiteGeneratorPaperData } from 'store/ui/actions'
import { setSelectedPapers } from 'store/add-report-dashboard-modal/actions'
import LinkResolverLink from './LinkResolverLink'
import { setMode } from '../../store/login-modal/actions'

import styles from './styles/Paper.css'
import FullTextLink from '../misc/FullTextLink'
import AGCheckAvailability from '../misc/AGCheckAvailability'

export const Paper = ({
  className,
  titleClassName,
  section,
  textMatch,
  paper,
  tally,
  selfCites,
  showLinksLayout = true,
  showCompactLinks = false
}) => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const handleAddToDashboard = () => {
    if (!paper) {
      return
    }

    if (!user) {
      dispatch(setMode('register'))
      dispatch(toggleModal('login', true, MODAL_FROM_CONTEXTS.ADD_REPORT_DASHBOARD))
      return
    }

    dispatch(setSelectedPapers([paper]))
    dispatch(toggleModal('addReportToDashboard'))
  }

  const handleGenerateCitation = () => {
    dispatch(setCiteGeneratorPaperData({
      citeGeneratorPaperData: paper
    }))
    dispatch(toggleModal('generateCitation'))
  }

  return (
    <div className={classNames(styles.paper, className)}>
      <Link
        key='titleEl'
        className={classNames(styles.title, titleClassName)}
        to={paper?.refCheckId ? `/reference-check/${paper?.refCheckId}` : `/reports/${paper?.slug || paper?.doi}`}
        bold
      >
        <h3 className={classNames(styles.title, titleClassName)} dangerouslySetInnerHTML={{ __html: highlightText(paper?.title || 'Untitled', textMatch) }} />
      </Link>
      <div className={styles.referenceLine}>
        <Authors className={styles.lineItem} textMatch={textMatch} authors={paper?.authors || []} year={paper?.year} truncated firstNames={false} />
        <span className={styles.lineItem}>
          <Reference
            journal={highlightText(paper?.journal, textMatch)}
            journalSlug={paper?.journalSlug}
            issns={paper?.issns}
            shortJournal={highlightText(paper?.shortJournal, textMatch)}
            issue={paper?.issue}
            page={paper?.page}
            section={highlightText(section, textMatch)}
            pmPublicationType={paper?.pmPublicationType}
            editorialNotices={paper?.editorialNotices}
            doi={paper?.doi}
            type={paper?.type}
            selfCites={selfCites}
            memberId={paper?.memberId}
          />
        </span>
      </div>
      {tally && (
        <div className={styles.tallyLayout}>
          <HorizontalTally notices={paper?.editorialNotices} tally={tally} link={`/reports/${paper?.slug || paper?.doi}`} />
        </div>
      )}
      {showLinksLayout && paper?.doi && (
        <div className={styles.linksLayout}>
          <FullTextLink className={classNames(styles.link, { [styles.compactLink]: showCompactLinks })} doi={paper?.doi} memberId={paper?.memberId} issns={paper?.issns} />
          <Link blue underline className={classNames(styles.link, { [styles.compactLink]: showCompactLinks })} onClick={handleAddToDashboard}>
            Add to dashboard
          </Link>
          <Link blue underline className={classNames(styles.link, { [styles.compactLink]: showCompactLinks })} onClick={handleGenerateCitation}>
            Cite
          </Link>
          <AGCheckAvailability doi={paper?.doi} showCompactLinks={showCompactLinks} />
          <LinkResolverLink doi={paper?.doi} issns={paper?.issns} memberId={paper?.memberId} />
        </div>
      )}
    </div>
  )
}

Paper.propTypes = {
  section: PropTypes.string,
  href: PropTypes.string,
  showTally: PropTypes.bool,

  paper: PropTypes.shape({
    title: PropTypes.string.isRequired,
    doi: PropTypes.string,
    authors: PropTypes.array,

    slug: PropTypes.string,
    year: PropTypes.number,
    shortJournal: PropTypes.string,
    issue: PropTypes.string,
    page: PropTypes.string,
    pmPublicationType: PropTypes.string,
    issns: PropTypes.arrayOf(
      PropTypes.string
    ),

    // NOTE: These come from the journal table
    journal: PropTypes.string,
    journalSlug: PropTypes.string,

    selfCites: PropTypes.array,
    user: PropTypes.object
  })
}

export default Paper
